import * as React from "react";
import ReactDOM from "react-dom";
import styles from "./AlertDialog.module.scss";
import { Button } from "../Button/Button";
import TimesIcon from "../../icons/Times";

export interface AlertDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    description: React.ReactNode;
    saveText?: string;
    cancelText?: string;
    onSaveClick?: () => void;
    onCancelClick?: () => void;
    isCancelBtnDisabled?: boolean;
    isSaveBtnDisabled?: boolean;
    isDeleting?: boolean;
    showCancelButton?: boolean;
    hideFooter?: boolean;
}

const FOCUSABLE_SELECTOR =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let nextDialogId = 0;

export const AlertDialog: React.FC<AlertDialogProps> = ({
    open,
    onClose,
    title,
    description,
    saveText = "Save",
    cancelText = "Cancel",
    onSaveClick,
    onCancelClick,
    isCancelBtnDisabled,
    isSaveBtnDisabled,
    isDeleting,
    showCancelButton = true,
    hideFooter = false,
}) => {
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const [dialogId] = React.useState(() => `alert-dialog-${++nextDialogId}`);

    // Move focus into the dialog when it opens and restore it when it closes
    React.useEffect(() => {
        if (!open) return;
        const previouslyFocused = document.activeElement as HTMLElement | null;
        dialogRef.current?.focus();
        return () => {
            previouslyFocused?.focus?.();
        };
    }, [open]);

    // Lock body scroll when dialog is open
    React.useEffect(() => {
        if (open) {
            const originalStyle = window.getComputedStyle(document.body).overflow;
            document.body.style.overflow = "hidden";

            return () => {
                document.body.style.overflow = originalStyle;
            };
        }
    }, [open]);

    // Close on Escape key
    React.useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
                return;
            }
            // Keep keyboard focus inside the dialog
            if (e.key !== "Tab" || !dialogRef.current) return;
            const focusable = Array.from(
                dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
            );
            if (focusable.length === 0) {
                e.preventDefault();
                dialogRef.current.focus();
                return;
            }
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement;
            if (e.shiftKey && (active === first || active === dialogRef.current)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && active === last) {
                e.preventDefault();
                first.focus();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose]);

    // Close on click outside
    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === overlayRef.current) {
            onClose();
        }
    };

    if (!open) return null;

    return ReactDOM.createPortal(
        <div
            className={styles.overlay}
            ref={overlayRef}
            onClick={handleOverlayClick}
            tabIndex={-1}
        >
            <div
                className={styles.dialog}
                ref={dialogRef}
                tabIndex={-1}
                role="alertdialog"
                aria-modal="true"
                aria-labelledby={`${dialogId}-title`}
                aria-describedby={`${dialogId}-description`}
            >
                <div className={styles.header}>
                    <div className={styles.title} id={`${dialogId}-title`}>
                        {title}
                    </div>
                    <Button
                        size="icon"
                        variant="normal"
                        type="button"
                        title="Close"
                        aria-label="Close"
                        onClick={onClose}
                    >
                        <TimesIcon />
                    </Button>
                </div>
                <div className={styles.content}>
                    <div
                        className={styles.description}
                        id={`${dialogId}-description`}
                    >
                        {description}
                    </div>
                </div>
                {!hideFooter && (
                    <div className={styles.footer}>
                        {showCancelButton && (
                            <Button
                                variant="secondary"
                                onClick={onCancelClick || onClose}
                                disabled={isCancelBtnDisabled}
                            >
                                {cancelText}
                            </Button>
                        )}
                        <Button
                            variant={isDeleting ? "danger" : "primary"}
                            onClick={onSaveClick}
                            disabled={isSaveBtnDisabled}
                        >
                            {saveText}
                        </Button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};
